import { cn } from '@/lib/utils';
import { useDialog } from '@/lib/useDialog';
import { X } from 'lucide-react';
import { ReactNode, useEffect, useId } from 'react';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
    size?: 'sm' | 'md' | 'lg';
    // Defaults to true; set false to require explicit close (e.g. mid-task).
    closeOnBackdropClick?: boolean;
    className?: string;
}

const SIZE = {
    sm: 'w-[420px]',
    md: 'w-[480px]',
    lg: 'w-[640px]',
} as const;

// Centered modal dialog. Handles Escape, focus trap, focus return,
// scroll-lock, scrim, and labelled header. Use for centered dialogs;
// slide-in drawers (HoldingDetailDrawer, AgentPanel) have their own
// shells but can share useDialog() for the same a11y guarantees.
export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    closeOnBackdropClick = true,
    className,
}: ModalProps) {
    const ref = useDialog({ isOpen, onClose });
    const titleId = useId();

    useEffect(() => {
        if (!isOpen) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                aria-hidden="true"
                className="absolute inset-0 bg-[rgb(var(--color-text-primary)/0.35)]"
                onClick={closeOnBackdropClick ? onClose : undefined}
            />
            <div
                ref={ref}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className={cn(
                    'relative bg-[rgb(var(--color-surface))] rounded-card shadow-xl flex flex-col max-h-[85vh]',
                    SIZE[size],
                    'max-w-full',
                    className,
                )}
            >
                <header className="flex items-center justify-between px-5 py-4 border-b border-[rgb(var(--color-divider))]">
                    <h2
                        id={titleId}
                        className="text-section-title text-[rgb(var(--color-text-primary))]"
                    >
                        {title}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close dialog"
                        className="p-1 rounded hover:bg-[rgb(var(--color-surface-secondary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent))]"
                    >
                        <X className="w-4 h-4 text-[rgb(var(--color-text-tertiary))]" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

                {footer && (
                    <footer className="px-5 py-3 border-t border-[rgb(var(--color-divider))] flex items-center justify-end gap-3">
                        {footer}
                    </footer>
                )}
            </div>
        </div>
    );
}
